import { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import cls from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useVirtualizer } from '@tanstack/react-virtual';

import FancyTooltip from '@/common/components/FancyTooltip/FancyTooltip';
import ModalTreeItem from '@/common/components/ModalTreeItem/ModalTreeItem';
import { TreeItemView } from '@/common/components/Tree/TreeItemView/TreeItemView';
import { flattenTree, removeChildrenOf } from '@/common/components/Tree/TreeItemView/TreeItemView.utils';
import { useVirtualScrollArea } from '@/common/hooks/useVirtualScrollArea/useVirtualScrollArea';
import { DriveItemId } from '@/drive/entities/DriveItemId';
import { DriveTreeItem } from '@/drive/entities/DriveTreeItem/DriveTreeItem';

import { useDriveModalTreeStore } from '../DriveModalTreeStore';

import styles from './BaseDriveModalTree.module.css';
import DriveModalTreeItemMeta from './DriveModalTreeItemMeta/DriveModalTreeItemMeta';
import DriveModalTreeItemPreview from './DriveModalTreeItemPreview/DriveModalTreeItemPreview';

interface DriveModalTreeProps {
  items: DriveTreeItem[];
}

const ESTIMATED_ROW_HEIGHT = 66;
const OVERSCAN_ROWS = 8;
const DISABLED_REASON_TOOLTIP_DELAY = 500;

function BaseDriveModalTree(props: DriveModalTreeProps) {
  const { items } = props;

  const {
    selectedId,
    setSelectedId,
    toggleCollapse,
    disabledReasons,
    isPendingCreation,
    finishCreation,
    editTitle,
    newItem,
  } = useDriveModalTreeStore();

  const flattenedItems = useMemo(() => {
    const flattenedItems = flattenTree(items);
    const collapsedItems = flattenedItems.reduce<StringOrNumber[]>(
      (acc, { collapsed, original, id }) => (collapsed && original.children?.length ? [...acc, id] : acc),
      [],
    );

    return removeChildrenOf(flattenedItems, collapsedItems);
  }, [selectedId, items]);

  const [listRef, { scrollElement, scrollMargin }] = useVirtualScrollArea();

  const virtualizer = useVirtualizer({
    count: flattenedItems.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    getItemKey: (index) => flattenedItems[index].id,
    overscan: OVERSCAN_ROWS,
    scrollMargin,
  });

  const editedItemIndex = newItem ? flattenedItems.findIndex(({ id }) => id === newItem.id) : -1;

  useEffect(() => {
    if (editedItemIndex >= 0) {
      virtualizer.scrollToIndex(editedItemIndex);
    }
  }, [editedItemIndex]);

  return (
    <div className={styles.list} ref={listRef} style={{ height: virtualizer.getTotalSize() }}>
      {virtualizer.getVirtualItems().map(({ index, start }) => {
        const { id, collapsed, original, depth } = flattenedItems[index];
        const isEdited = original.id === newItem?.id && !isPendingCreation;
        const isSelected = original.id === selectedId;
        const disabledReason = disabledReasons.get(id);
        const isDisabled = !!disabledReason;
        const value = isEdited ? (newItem?.title ?? original.title) : original.title;

        return (
          <div
            className={styles.row}
            key={id}
            data-index={index}
            ref={virtualizer.measureElement}
            style={{ transform: `translateY(${start - scrollMargin}px)` }}
          >
            <TreeItemView
              item={original}
              depth={depth}
              indentationWidth={15}
              className={cls({ [styles.disabledItem]: isDisabled })}
              selected={isSelected}
              collapsed={collapsed && !isEmpty(original.children)}
              onSelect={isDisabled ? undefined : () => setSelectedId(original.id as DriveItemId)}
              onCollapse={!isEmpty(original.children) ? () => toggleCollapse(original) : undefined}
              renderItem={() => (
                <FancyTooltip
                  content={disabledReason}
                  className={styles.disabledReasonTooltip}
                  placement="bottom"
                  openDelay={DISABLED_REASON_TOOLTIP_DELAY}
                  usePortal
                >
                  <div>
                    <ModalTreeItem
                      isEdited={isEdited}
                      value={value}
                      isSelected={isSelected}
                      onChange={editTitle}
                      save={finishCreation}
                      isPendingCreation={isPendingCreation}
                      preview={<DriveModalTreeItemPreview item={original} />}
                      footer={<DriveModalTreeItemMeta item={original} />}
                    />
                  </div>
                </FancyTooltip>
              )}
            />
          </div>
        );
      })}
    </div>
  );
}

export default observer(BaseDriveModalTree);
